/* eslint-disable @typescript-eslint/no-this-alias */
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import {
  BulkImportType,
  ChartDataPoint,
  DataChip,
  DataSeries,
  GraphDataPoint,
  GraphDataResponse,
  GraphTimeSeriesData
} from '@dr-customer-offers-ui/lib-interfaces';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import * as Highcharts from 'highcharts';
import ExportData from 'highcharts/modules/export-data';
import { default as ExportingModule, default as HC_exporting } from 'highcharts/modules/exporting';
import OfflineExporting from 'highcharts/modules/offline-exporting';
import moment from 'moment-timezone';
import { combineLatest, filter, startWith } from 'rxjs';
import { TabOption } from '../../../../../../src/app/shared/models/tab.model';
import { DataViewModelService } from '../../../../shared/services/data-vm.service';
import { DateService } from '../../../../shared/services/date.service';
import { InternalService } from '../../../../shared/services/internal.service';
import * as Actions from '../../../../store/actions/com.actions';
import * as Selectors from '../../../../store/selectors/com.selectors';
import { FiltersStateService } from '../../../filters/filters-state.service';
import { ClearedTabService } from '../cleared-tab.service';

HC_exporting(Highcharts);
ExportingModule(Highcharts);
OfflineExporting(Highcharts);
ExportData(Highcharts);

@Component({
  selector: 'dr-customer-offers-ui-cleared-chart',
  templateUrl: './cleared-tab-chart.component.html',
  styleUrls: ['./cleared-tab-chart.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ClearedChartComponent implements OnInit {
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);

  Highcharts: typeof Highcharts = Highcharts;
  chartRef: Highcharts.Chart | undefined;
  // Signals for reactive state management with OnPush
  private offerResponseSignal: WritableSignal<GraphDataResponse | null> = signal(null);
  private chartSeriesSignal = signal<DataSeries[]>([]);
  private availableChipsSignal = signal<DataChip[]>([]);
  private chartUpdateTrigger = signal<number>(0);
  private apiMetadata = signal<any>(null);
  private currentDateSignal = signal<moment.Moment | null>(null);
  private originalApiDataSignal = signal<GraphTimeSeriesData[]>([]);
  protected submitLoadingSignal: WritableSignal<boolean> = signal(false);

  protected minDate!: moment.Moment;
  protected maxDate!: moment.Moment;

  private clearedGraphOffersLoading = toSignal(this.store.select(Selectors.selectClearedGraphOffersLoading), { initialValue: false });
  private selectedRegistration = toSignal(this.store.select(Selectors.selectSelectedRegistration), { initialValue: null });

  private currentRegViewConfigSignal = toSignal(this.store.select(Selectors.selectCurrentRegViewConfig), { initialValue: null });
  private timeZoneSignal = computed(() => this.currentRegViewConfigSignal()?.timeZone || 'UTC');
  protected dstStatusSignal = this.dateService.dstStatusSignal;
  protected isConsolidatedToHourlySignal = computed<boolean>(() => this.clearedService.isConsolidatedToHourly(this.currentRegViewConfigSignal()));

  availableChips = this.availableChipsSignal.asReadonly();
  private currencySignal = computed(() => {
    const regConfig = this.currentRegViewConfigSignal();
    return regConfig?.timeZone === 'Europe/Warsaw' ? 'PLN' : 'EUR';
  });
  protected showDownloadUploadOptionsSignal = signal(false);

  protected isBulkImportAllowed = computed(() => {
    const allowImport = this.currentRegViewConfigSignal()?.gateRules?.post_gate_edits?.allow_offer_edit;
    console.log('allowImport:', !!allowImport);
    console.log('currentRegViewConfig:', !!this.currentRegViewConfigSignal()?.allow_cleared_offers_bulk_upload);
    return !!allowImport && !!this.currentRegViewConfigSignal()?.allow_cleared_offers_bulk_upload;
  });
  form: FormGroup;

  get selectedTabSignal(): WritableSignal<TabOption> {
    return this.dataVMService.selectedTabOptionSignal;
  }

  // Computed chart options that automatically update when chips change
  chartOptions = computed(() => {
    // Trigger dependency on chartUpdateTrigger for forced updates
    this.chartUpdateTrigger();

    // If no data, return empty chart configuration
    if (!this.hasDataSignal()) {
      return {
        ...this.getBaseChartOptions(),
        series: []
      } as Highcharts.Options;
    }

    const visibleSeries = this.chartSeriesSignal()
      .filter((series) => series.visible)
      .map((series) => {
        const baseSeriesOptions: any = {
          id: series.id,
          name: series.name,
          data: series.data,
          color: series.color,
          type: series.chartType,
          unit: series.unit, // Custom property for tooltip
          cursor: 'pointer' // Show pointer cursor for clickable data points
        };

        // Add type-specific properties conditionally
        if (series.chartType === 'line') {
          baseSeriesOptions.lineWidth = 2;
          baseSeriesOptions.marker = {
            enabled: false,
            states: {
              hover: {
                enabled: true,
                radius: 4
              }
            }
          };
        } else if (series.chartType === 'column') {
          baseSeriesOptions.borderWidth = 1; // Add border to each column
          baseSeriesOptions.borderColor = '#ffffff'; // White border for contrast
          baseSeriesOptions.pointPadding = 0; // No space between bars
          baseSeriesOptions.groupPadding = 0; // No space between groups
          baseSeriesOptions.borderRadius = 0; // Square corners
        }

        return baseSeriesOptions;
      });

    return {
      ...this.getBaseChartOptions(),
      series: visibleSeries
    } as Highcharts.Options;
  });

  // Component state signals
  loadingSignal = computed(() => this.clearedGraphOffersLoading());
  hasDataSignal = signal<boolean>(true);
  userLocale = computed(() => this.internalService.userPreferenceSignal()?.locale ?? 'en-US');

  constructor(
    private dataVMService: DataViewModelService,
    private fb: FormBuilder,
    private filtersStateService: FiltersStateService,
    private translate: TranslateService,
    private dateService: DateService,
    private internalService: InternalService,
    private clearedService: ClearedTabService
  ) {
    this.form = this.fb.group({
      currentDate: moment.utc(this.dataVMService.getAppliedDayRange()?.start)
    });

    this.currentDateSignal.set(moment.utc(this.currentDateFormControl.value));

    this.currentDateFormControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((value) => {
      this.currentDateSignal.set(moment.utc(value));
    });

    console.log('isConsolidatedToHourlySignal', this.isConsolidatedToHourlySignal());
  }

  get currentDateFormControl(): FormControl<moment.Moment> {
    return this.form.get('currentDate') as FormControl<moment.Moment>;
  }

  get formattedCurrentDate(): string {
    const date = this.currentDateFormControl.value;
    const locale = this.userLocale();
    return date ? date.locale(locale).format('L (ddd)') : '';
  }

  ngOnInit(): void {
    this.dataVMService.selectedFromDayPickerFormControl.valueChanges
      .pipe(startWith(this.dataVMService.selectedFromDayPickerFormControl.value), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        const appliedStart = this.dataVMService.getAppliedDayRange()?.start;
        if (appliedStart) {
          this.currentDateFormControl.setValue(appliedStart, { emitEvent: false });
        }
      });

    combineLatest([this.filtersStateService.applyClicked$, this.store.select(Selectors.selectCurrentRegViewConfig)])
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(([filters, config]) => filters !== null && config !== null)
      )
      .subscribe(() => {
        const currentSelectedDate = this.dataVMService.getAppliedDayRange()?.start;
        if (currentSelectedDate) {
          this.currentDateFormControl.setValue(currentSelectedDate, { emitEvent: false });
          this.loadGraphData(currentSelectedDate);
        }
      });

    this.store
      .select(Selectors.selectCurrentClearedGraphOffers)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response: GraphDataResponse | null) => {
        this.offerResponseSignal.set(response);
        this.processApiResponse(response);
        this.filtersStateService.setApplyButtonChanging(this.dataVMService.selectedRegFormControl.value);
      });

    // Handle loading errors
    this.store
      .select(Selectors.selectClearedGraphOffersError)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((error) => error !== null)
      )
      .subscribe(() => {
        this.offerResponseSignal.set(null);
        this.hasDataSignal.set(false);
      });
  }

  /**
   * Load graph data from API response
   */
  private loadGraphData(currentDate: moment.Moment): void {
    const utcString: string = currentDate.format('YYYY-MM-DDTHH:mm:ss.SSS') + 'Z';

    const registration = this.selectedRegistration();
    if (!registration) {
      console.warn('No registration selected');
      return;
    }

    // Dispatch action to load graph offers
    this.store.dispatch(
      Actions.loadClearedGraphOffers({
        registrationId: registration.id,
        currentDate: utcString
      })
    );
  }

  /**
   * Process API response and convert to chart format
   */
  private processApiResponse(response: GraphDataResponse | null): void {
    if (!response || !response.timeSeriesData) {
      console.warn('Invalid API response structure');
      this.hasDataSignal.set(false);
      return;
    }

    // Store metadata for chart configuration
    this.apiMetadata.set(response.metadata);

    const isHourly = this.isConsolidatedToHourlySignal();
    const timeSeriesData = response.timeSeriesData.filter(
      (series: GraphTimeSeriesData) => !this.clearedService.isSeriesHiddenForMarket(series.name, isHourly)
    );

    // Create chips based on API data
    const chips: DataChip[] = timeSeriesData.map((series: GraphTimeSeriesData) => {
      const seriesConfig = this.getSeriesConfiguration(series.name);
      return {
        id: series.name.toLowerCase().replace(/\s+/g, '-'),
        label: series.name,
        selected: seriesConfig.defaultVisible,
        color: seriesConfig.color,
        unit: response.metadata.unit || '',
        chartType: seriesConfig.chartType
      };
    });

    this.availableChipsSignal.set(chips.filter((chip) => chip.label !== 'Price'));

    // Store original API data for timestamp access
    this.storeOriginalApiData(timeSeriesData);

    // Convert API data to chart series
    const chartSeries = timeSeriesData
      .map((apiSeries: GraphTimeSeriesData) => {
        const chip = chips.find((c: DataChip) => c.label === apiSeries.name);
        if (!chip) return null;

        // Convert all data points (including nulls as 0)
        const data: ChartDataPoint[] = apiSeries.dataPoints
          .map((point: GraphDataPoint) => ({
            x: moment.utc(point.start_dttm_utc).valueOf(),
            y: point.value !== null ? point.value : 0 // Convert null to 0
          }))
          .sort((a, b) => a.x - b.x);

        return {
          id: chip.id,
          name: chip.label,
          displayName: chip.label,
          color: chip.color,
          data: data,
          unit: chip.unit,
          visible: chip.selected,
          chartType: chip.chartType
        };
      })
      .filter((series) => series !== null) as DataSeries[];

    this.chartSeriesSignal.set(chartSeries);
    this.hasDataSignal.set(true);
  }

  /**
   * Store original API data for timestamp lookups
   */
  private storeOriginalApiData(timeSeriesData: GraphTimeSeriesData[]): void {
    this.originalApiDataSignal.set(timeSeriesData);
  }

  /**
   * Get series configuration (color and chart type) based on series name
   */
  private getSeriesConfiguration(seriesName: string): { color: string; chartType: 'line' | 'column' | 'bar'; defaultVisible: boolean } {
    const name = seriesName?.toLowerCase();

    if (name.includes('capacity')) {
      return {
        color: '#D7E5FE',
        chartType: 'column',
        defaultVisible: true
      };
    } else if (name.includes('demand')) {
      return {
        color: '#CD1873',
        chartType: 'line',
        defaultVisible: true
      };
    } else {
      return {
        color: '#FF6B6B',
        chartType: 'column',
        defaultVisible: false
      };
    }
  }

  private getTickInterval(): number {
    const millisecondsPerMinute = 60 * 1000;
    const labelIntervalMinutes = 60;
    return labelIntervalMinutes * millisecondsPerMinute;
  }

  /**
   * Get base chart options configuration
   */
  private getBaseChartOptions(): Omit<Highcharts.Options, 'series'> {
    const tickInterval = this.getTickInterval();
    const metadata = this.apiMetadata();
    const componentRef = this;
    const userTimeZone = this.timeZoneSignal();

    return {
      // Chart configuration with click event
      chart: {
        type: 'line',
        backgroundColor: '#ffffff',
        style: {
          fontFamily: 'Roobert, RoobertENEL-Light, sans-serif !important;'
        },
        height: 400,
        width: null,
        reflow: true
      },

      // Add plot options for series click events
      plotOptions: {
        line: {
          dataLabels: { enabled: false },
          enableMouseTracking: true,
          pointPlacement: 'between',
          marker: {
            enabled: false,
            states: {
              hover: { enabled: true, radius: 4 }
            }
          }
        },
        column: {
          dataLabels: { enabled: false },
          enableMouseTracking: true,
          borderRadius: 0,
          pointPadding: 0,
          groupPadding: 0,
          borderWidth: 1,
          borderColor: '#ffffff',
          cursor: 'pointer',
          pointPlacement: 'between',
          states: {
            hover: { brightness: 0.1 }
          }
        },
        series: {
          turboThreshold: 3000,
          states: {
            hover: {
              enabled: true,
              lineWidthPlus: 1
            }
          }
        }
      },

      exporting: {
        enabled: true,
        fallbackToExportServer: false,
        buttons: {
          contextButton: {
            enabled: true,
            symbol: 'menu',
            menuItems: ['viewFullscreen', 'separator', 'downloadPNG', 'downloadJPEG', 'downloadPDF', 'downloadSVG']
          }
        },
        menuItemDefinitions: {
          downloadPNG: {
            text: 'Download PNG',
            onclick: function () {
              componentRef.forceExportChart('image/png');
            }
          },
          downloadJPEG: {
            text: 'Download JPEG',
            onclick: function () {
              componentRef.forceExportChart('image/jpeg');
            }
          },
          downloadPDF: {
            text: 'Download PDF',
            onclick: function () {
              componentRef.forceExportChart('application/pdf');
            }
          },
          downloadSVG: {
            text: 'Download SVG',
            onclick: function () {
              componentRef.forceExportChart('image/svg+xml');
            }
          }
        },
        sourceWidth: 1280,
        sourceHeight: 500,
        scale: 2
      },

      title: {
        text: '',
        align: 'left',
        style: {
          color: '#333333',
          fontSize: '18px',
          fontWeight: 'bold'
        }
      },

      credits: { enabled: false },

      // X-axis configuration
      xAxis: {
        type: 'datetime',
        crosshair: true,
        tickWidth: 1,
        tickLength: 6,
        tickInterval: tickInterval,
        minTickInterval: tickInterval,
        labels: {
          style: {
            color: '#666666',
            fontSize: '12px'
          },
          formatter: function () {
            return moment.utc(this.value).tz(userTimeZone).format('HH:mm');
          },
          rotation: 0,
          overflow: 'justify',
          step: 1
        },
        title: {
          text: '',
          style: {
            color: '#666666',
            fontSize: '14px'
          }
        },
        gridLineWidth: 1,
        gridLineColor: '#f0f0f0'
      },

      // Y-axis configuration
      yAxis: {
        title: {
          text: metadata?.unit || 'Value',
          style: {
            color: '#666666',
            fontSize: '14px'
          }
        },
        labels: {
          style: {
            color: '#666666',
            fontSize: '12px'
          }
        },
        gridLineColor: '#e0e0e0',
        gridLineWidth: 1
      },

      legend: {
        enabled: false
      },

      // Tooltip configuration (existing)
      tooltip: {
        enabled: true,
        shared: true,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#cccccc',
        borderRadius: 5,
        shadow: true,
        useHTML: true,
        padding: 15,
        hideDelay: 500,
        distance: 30,
        style: {
          fontSize: '14px',
          fontFamily: 'Roobert, RoobertENEL-Light, sans-serif',
          lineHeight: '1.4',
          padding: '12px 16px',
          minWidth: '220px',
          maxWidth: '380px',
          minHeight: '80px',
          whiteSpace: 'normal',
          wordWrap: 'break-word'
        },
        formatter: function () {
          const chartMetadata = componentRef.apiMetadata() || { frequency: 15 };
          const frequency = chartMetadata.frequency || 15;
          const userTz = componentRef.timeZoneSignal();
          const currentMoment = moment.utc(this.x).tz(userTz);
          const nextMoment = currentMoment.clone().add(frequency, 'minutes');
          const dateString = currentMoment.format('ddd Do MMMM');
          const timeRange = `${currentMoment.format('HH:mm')} - ${nextMoment.format('HH:mm')}`;
          const clearedCapacityLabel = componentRef.translate.instant('COM.TABS.CLEARED.CLEARED_CAPACITY');
          const clearedDemandLabel = componentRef.translate.instant('COM.TABS.CLEARED.CLEARED_DEMAND');
          const priceLabel = componentRef.translate.instant('COM.TABS.CLEARED.PRICE');

          let tooltip = `
                <div style="
                  min-width: 14.375rem;
                  display: flex;
                  justify-content: space-between;
                  align-items: center;
                  margin-bottom: 0.75rem;
                  font-size: 0.75rem;
                  color: #666;
                  font-weight: 500;
                ">
                  <span>${dateString}</span>
                  <span>${timeRange}</span>
                </div>
              `;

          // Get all series data including invisible ones
          const allSeries = componentRef.chartSeriesSignal();
          const currentX = this.x;

          allSeries.forEach((series: DataSeries) => {
            const dataPoint = series.data.find((point) => point.x === currentX);
            if (dataPoint) {
              const value =
                dataPoint.y !== null && dataPoint.y !== undefined
                  ? `${dataPoint.y.toFixed(0)} ${series.displayName === 'Price' ? componentRef.currencySignal() : series.unit || ''}`
                  : '-';

              tooltip += `
                  <div style="
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin: 0.5rem 0;
                    font-size: 0.875rem;
                    line-height: 1.4;
                  ">
                    <div style="
                      display: flex;
                      align-items: center;
                      color: #333;
                    ">
                      <span style="
                        color: ${series.color};
                        margin-right: 0.5rem;
                        font-size: 0.75rem;
                        width: 0.5rem;
                        height: 0.5rem;
                        border-radius: 50%;
                        background-color: ${series.color};
                        display: inline-block;
                      "></span>
                      <span style="font-weight: 500;">${series.name === 'Capacity' ? clearedCapacityLabel : series.name === 'Demand' ? clearedDemandLabel : series.displayName}</span>
                    </div>
                    <div style="
                      font-weight: 600;
                      color: #333;
                      text-align: right;
                    ">
                      ${value}
                    </div>
                  </div>
                `;
            }
          });

          return `
                <div style="
                  min-width: 11.25rem;
                  padding: 0.75rem;
                  line-height: 1.4;
                ">
                  ${tooltip}
                </div>
              `;
        }
      }
    } as Highcharts.Options;
  }

  /**
   * Chart reference callback for highcharts-angular
   */
  chartCallback: Highcharts.ChartCallbackFunction = (chart: Highcharts.Chart): void => {
    this.chartRef = chart;

    // Only update if we have data
    if (this.hasDataSignal() && this.chartSeriesSignal().length > 0) {
      this.updateChartManually();
    }
  };

  get getChipStyle() {
    return {
      'background-color': 'rgb(245, 246, 250)',
      'border-color': 'rgb(182, 195, 213)',
      color: '#000'
    };
  }

  private updateChartManually(): void {
    if (!this.chartRef) {
      console.log('Chart ref not available yet');
      return;
    }

    while (this.chartRef.series.length > 0) {
      this.chartRef.series[0].remove(false);
    }

    const visibleSeries = this.chartSeriesSignal().filter((series) => series.visible);

    visibleSeries.forEach((series: DataSeries) => {
      const seriesOptions: any = {
        id: series.id,
        name: series.name,
        data: series.data,
        color: series.color,
        type: series.chartType,
        unit: series.unit
      };

      if (series.chartType === 'line') {
        seriesOptions.lineWidth = 2;
        seriesOptions.marker = {
          enabled: false,
          states: {
            hover: { enabled: true, radius: 4 }
          }
        };
      } else if (series.chartType === 'column') {
        seriesOptions.borderWidth = 1;
        seriesOptions.borderColor = '#ffffff';
        seriesOptions.pointPadding = 0;
        seriesOptions.groupPadding = 0;
        seriesOptions.borderRadius = 0;
      }

      this.chartRef!.addSeries(seriesOptions, false);
    });

    this.chartRef.redraw();
  }

  private forceExportChart(exportType: 'image/png' | 'image/jpeg' | 'application/pdf' | 'image/svg+xml'): void {
    if (!this.chartRef || !this.hasDataSignal()) {
      return;
    }

    const currentOptions: Highcharts.Options = this.chartOptions();

    const exportSeries = this.chartSeriesSignal()
      .filter((series: DataSeries) => series.visible)
      .map((series: DataSeries) => {
        const seriesOptions: any = {
          id: series.id,
          name: series.name,
          data: series.data,
          color: series.color,
          type: series.chartType
        };

        if (series.chartType === 'column') {
          seriesOptions.borderWidth = 1;
          seriesOptions.borderColor = '#ffffff';
          seriesOptions.pointPadding = 0;
          seriesOptions.groupPadding = 0;
        }

        return seriesOptions;
      });

    const exportOptions: Highcharts.Options = {
      ...currentOptions,
      chart: currentOptions.chart,
      series: exportSeries
    };

    const tempDiv = document.createElement('div');
    tempDiv.style.width = '1200px';
    tempDiv.style.height = '600px';
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    document.body.appendChild(tempDiv);

    const tempChart = Highcharts.chart(tempDiv, exportOptions);

    tempChart.exportChart(
      {
        type: exportType as any,
        filename: `offers-${moment().format('YYYY-MM-DD_HHmmss')}`
      },
      {}
    );

    tempChart.destroy();
    document.body.removeChild(tempDiv);
  }

  // ---------------------------------------------------------------------------
  // Download / Upload menu
  // ---------------------------------------------------------------------------

  toggleDownloadUploadOptions(event: MouseEvent): void {
    event.stopPropagation();
    this.showDownloadUploadOptionsSignal.update((show) => !show);
  }

  clickOutsideDownloadUploadOptions(): void {
    this.showDownloadUploadOptionsSignal.update((show) => (show ? false : show));
  }

  openBulkUploadFunctionalityDialog(): void {
    const regConfig = this.currentRegViewConfigSignal();
    this.dataVMService.openBulkUploadFunctionalityDialog({
      minDate: this.minDate,
      maxDate: this.maxDate,
      registrationId: regConfig?.registrationId ?? '',
      intervalFrequency: regConfig?.intervalFrequency ?? 60,
      timeZone: regConfig?.timeZone ?? 'UTC',
      importType: BulkImportType.Offers,
      registrationName: regConfig?.display_labels ?? '',
      offer_dimensions: regConfig?.graph_config?.offer_dimensions ?? null
    });
  }
}
